import { Extension } from '@tiptap/core'
import { Fragment } from '@tiptap/pm/model'
import { Plugin, PluginKey, TextSelection } from '@tiptap/pm/state'
import { insertPoint } from '@tiptap/pm/transform'
import { Decoration, DecorationSet } from '@tiptap/pm/view'
import { studioLabel } from '../studioContext.js'
import { isSinglePictureHtml, isWordHtml, transformWordHtml, wordPictures } from '../wordPaste.js'
import { newPictureAttrs } from './studioNodes.js'

/**
 * Pictures and pastes from outside the Studio (RED-317 PR 3b).
 *
 * - A picture file (the toolbar's picker, a drop, or a paste whose HTML is
 *   only that picture: a copied picture or chart) is uploaded through
 *   `uploadPicture` and becomes a new templateImage (contract §5). A grey
 *   "uploading" chip holds its place meanwhile.
 * - Word HTML goes through the paste transform (../wordPaste.js). Mac Word
 *   inlines its pictures and charts as data: URIs: those are uploaded first
 *   and pasted with the text. Windows Word points them at file:// paths a
 *   web page cannot read: they are dropped, and so is any other <img>.
 * - Every outcome the admin should know about goes to `onNotice` (shown
 *   inline by the editing UI; no toasts): { kind: 'picturesDropped', count }
 *   or { kind: 'pictureRefused', message }. `onUploadsChange(count)` follows
 *   the uploads in flight.
 *
 * Options: uploadPicture(file) → Promise<{ ok: true, picture } | { ok: false, message }>,
 * where picture is the endpoint's { mediaPart, widthEmu, heightEmu, … }.
 * Without an uploader, pictures are refused the same way as file:// ones.
 */

const uploadsKey = new PluginKey('studioPictureUploads')

/** Marks HTML that was already transformed (the Mac Word path pastes it again after uploading). */
const TRANSFORMED = '<!--studio-word-transformed-->'

const EMU_PER_PX = 9525

function uploadingChip() {
  const chip = document.createElement('span')
  chip.className = 'studio-picture-pending'
  chip.setAttribute('data-testid', 'studio-picture-pending')
  chip.textContent = studioLabel('pictureUploading')
  return chip
}

/** A data: URI (Mac Word's inlined picture) as a File for the upload. */
function dataUrlFile(src, type) {
  const base64 = src.slice(src.indexOf(',') + 1).replace(/\s+/g, '')
  const binary = atob(base64)
  const bytes = new Uint8Array(binary.length)
  for (let index = 0; index < binary.length; index++) bytes[index] = binary.charCodeAt(index)
  return new File([bytes], `word-picture.${type === 'image/jpeg' ? 'jpg' : type.split('/')[1]}`, { type })
}

/** Inline pictures at pos: in its paragraph, or a new paragraph where a block goes. */
function insertPictures(tr, pos, nodes) {
  const schema = tr.doc.type.schema
  const $pos = tr.doc.resolve(Math.min(pos, tr.doc.content.size))
  if ($pos.parent.inlineContent) {
    tr.insert($pos.pos, Fragment.fromArray(nodes))
    return
  }
  const paragraph = schema.nodes.paragraph.create(null, nodes)
  const at = insertPoint(tr.doc, $pos.pos, schema.nodes.paragraph)
  tr.insert(at ?? tr.doc.content.size, paragraph)
}

export const StudioPaste = Extension.create({
  name: 'studioPaste',

  // Before prosemirror-tables' paste handler, after StudioClipboard's.
  priority: 900,

  addOptions() {
    return {
      uploadPicture: null,
      onNotice: () => {},
      onUploadsChange: () => {},
    }
  },

  addStorage() {
    return { inFlight: 0 }
  },

  addCommands() {
    return {
      /** Upload picture files and insert them at pos (default: the end of the selection). */
      insertPictureFiles:
        (files, pos = null) =>
        ({ editor }) => {
          if (!files?.length) return false
          editor.storage.studioPaste.upload(editor.view, [...files], pos)
          return true
        },
    }
  },

  onCreate() {
    const { editor, options, storage } = this

    const track = (delta) => {
      storage.inFlight += delta
      options.onUploadsChange(storage.inFlight)
    }

    const send = async (file) => {
      if (!options.uploadPicture) return { ok: false, message: null }
      try {
        return await options.uploadPicture(file)
      } catch {
        return { ok: false, message: null }
      }
    }

    /** Files → pictures at pos, behind one uploading chip. */
    storage.upload = async (view, files, pos) => {
      const id = {}
      const at = pos ?? view.state.selection.to
      view.dispatch(view.state.tr.setMeta(uploadsKey, { add: { id, pos: at } }))
      track(1)
      const results = await Promise.all(files.map(send))
      track(-1)
      if (editor.isDestroyed) return

      const placed = uploadsKey.getState(view.state).find(undefined, undefined, (spec) => spec.id === id)[0]
      const tr = view.state.tr.setMeta(uploadsKey, { remove: id })
      const nodes = results
        .filter((result) => result.ok)
        .map((result) => newPictureAttrs(result.picture))
        .filter(Boolean)
        .map((attrs) => view.state.schema.nodes.templateImage.create(attrs))
      if (nodes.length) {
        insertPictures(tr, placed ? placed.from : view.state.selection.to, nodes)
      }
      view.dispatch(tr)
      for (const result of results.filter((item) => !item.ok)) {
        options.onNotice({ kind: 'pictureRefused', message: result.message ?? null })
      }
    }

    /** Mac Word: the data: pictures go up first, then the text is pasted with them. */
    storage.pasteWordWithPictures = async (view, html, pictures) => {
      track(1)
      const uploaded = new Map()
      const results = await Promise.all(pictures.map((picture) => send(dataUrlFile(picture.src, picture.type))))
      track(-1)
      if (editor.isDestroyed) return
      results.forEach((result, index) => {
        if (!result.ok) return
        const { width, height } = pictures[index]
        // Word's own size (px at 96 dpi) wins over the upload's natural size.
        const size = width && height ? { widthEmu: width * EMU_PER_PX, heightEmu: height * EMU_PER_PX } : {}
        uploaded.set(pictures[index].src, { ...result.picture, ...size })
      })
      const transformed = transformWordHtml(html, { pictures: uploaded })
      view.pasteHTML(TRANSFORMED + transformed.html)
      const refused = results.filter((result) => !result.ok)
      refused.forEach((result) => options.onNotice({ kind: 'pictureRefused', message: result.message ?? null }))
      if (transformed.droppedPictures > refused.length) {
        options.onNotice({ kind: 'picturesDropped', count: transformed.droppedPictures - refused.length })
      }
    }
  },

  addProseMirrorPlugins() {
    const { options, storage } = this

    // ProseMirror parses a paste (transformPastedHTML) BEFORE handlePaste or
    // handleDrop decide what happens to it, so a notice found while parsing is
    // held until they have: dropped when the upload path takes the paste over
    // (its pictures do come along), shown otherwise.
    let held = null
    const holdNotice = (notice) => {
      held = notice
      setTimeout(() => {
        if (held === notice) {
          held = null
          options.onNotice(notice)
        }
      })
    }
    const takeOver = () => {
      held = null
    }

    return [
      new Plugin({
        key: uploadsKey,
        state: {
          init: () => DecorationSet.empty,
          apply(tr, set) {
            let next = set.map(tr.mapping, tr.doc)
            const meta = tr.getMeta(uploadsKey)
            if (meta?.add) {
              next = next.add(tr.doc, [Decoration.widget(Math.min(meta.add.pos, tr.doc.content.size), uploadingChip, { id: meta.add.id, side: -1 })])
            }
            if (meta?.remove) {
              next = next.remove(next.find(undefined, undefined, (spec) => spec.id === meta.remove))
            }
            return next
          },
        },
        props: {
          decorations(state) {
            return uploadsKey.getState(state)
          },

          handlePaste(view, event) {
            const data = event.clipboardData
            if (!data) return false
            const html = data.getData('text/html')
            const files = [...(data.files ?? [])]
            if (files.length && isSinglePictureHtml(html)) {
              takeOver()
              storage.upload(view, files, null)
              return true
            }
            if (options.uploadPicture && isWordHtml(html) && !html.startsWith(TRANSFORMED)) {
              const pictures = wordPictures(html)
              if (pictures.length) {
                takeOver()
                storage.pasteWordWithPictures(view, html, pictures)
                return true
              }
            }
            return false
          },

          transformPastedHTML(html) {
            if (html.startsWith(TRANSFORMED)) return html.slice(TRANSFORMED.length)
            if (isWordHtml(html)) {
              const transformed = transformWordHtml(html)
              if (transformed.droppedPictures) holdNotice({ kind: 'picturesDropped', count: transformed.droppedPictures })
              return transformed.html
            }
            const dropped = (html.match(/<img\b(?![^>]*data-studio-picture)/gi) ?? []).length
            if (dropped) holdNotice({ kind: 'picturesDropped', count: dropped })
            return html
          },

          handleDrop(view, event, slice, moved) {
            const files = [...(event.dataTransfer?.files ?? [])]
            if (moved || !files.length) return false
            takeOver()
            event.preventDefault()
            const pos = view.posAtCoords({ left: event.clientX, top: event.clientY })?.pos ?? null
            if (pos !== null) view.dispatch(view.state.tr.setSelection(TextSelection.near(view.state.doc.resolve(pos))))
            storage.upload(view, files, pos)
            return true
          },
        },
      }),
    ]
  },
})
